/**
 * Pure seeding logic for the budgets editor, kept out of the component so it
 * can be unit-tested without a DOM.
 *
 * Both functions exist to consume the read-back fields the `/usage` endpoint
 * returns (`thresholds`, `roleCaps`). Before they were wired, the editor seeded
 * from hardcoded defaults and every save blindly replaced whatever was stored —
 * so an admin who reopened the editor saw the defaults, not their configuration,
 * and saving one field silently reset the others.
 */
import { CAP_ROLES, resolveRoleCap, type BudgetCap } from '@papan/types'
import type { OrgUsageResponse, RoleCapEntry } from '../../client/budget'
import { toDollars } from '@papan/core-ui/ui'

/** The documented default when a tenant has never configured thresholds. */
export const DEFAULT_THRESHOLD_PCT = [80, 100]

/** ───── Staged cap edits (A6) ──────────────────────────────────────────
 *
 * The cap tables used to write on blur. They now stage into a draft and commit
 * on an explicit Save, so a mistyped digit plus a stray click no longer sets a
 * money ceiling. The pure half lives here for the reason the file exists: the
 * traps below are exactly the kind a DOM test would not catch.
 */

/** Which table a staged row came from. The three write to different endpoints
 *  and are keyed by different things — a role id, a team id, an email. */
export type CapScope = 'role' | 'team' | 'member'

/** Staged edits, keyed by `capKey`. A map rather than a fixed struct because
 *  the rows are DATA (teams, members), not columns. */
export type CapDraft = Record<string, BudgetCap>

export function capKey(scope: CapScope, id: string): string {
  return `${scope}:${id}`
}

/**
 * Split a key back into its parts.
 *
 * Rejoins on `:` rather than taking `parts[1]`, because the member axis is
 * keyed by EMAIL and the local part of an address may legally contain a colon
 * inside a quoted string. Rare, but the failure would be a cap written for the
 * wrong member — silent, and in the permissive direction.
 */
export function parseCapKey(key: string): { scope: CapScope; id: string } | null {
  const idx = key.indexOf(':')
  if (idx <= 0) return null
  const scope = key.slice(0, idx)
  const id = key.slice(idx + 1)
  if (!id) return null
  if (scope !== 'role' && scope !== 'team' && scope !== 'member') return null
  return { scope, id }
}

/**
 * What to render for a row: the staged value if it has one, else the server's.
 *
 * KEY PRESENCE, never a falsy or nullish test. `0` is a real cap ("contributes
 * nothing" on the additive axes, "blocked" on a ceiling) and `null` is another
 * ("unlimited"). `draft[key] ?? current` would silently discard a staged
 * "unlimited" and redisplay the old number, which is the one edit an admin is
 * most likely to make and least likely to re-check.
 */
export function stagedCap(draft: CapDraft, key: string, current: BudgetCap): BudgetCap {
  return key in draft ? draft[key] : current
}

/**
 * Which staged rows actually differ from what the server holds.
 *
 * Measured against CURRENT server truth on every render, not against whatever
 * was there when staging began, so:
 *  • typing a value and typing it back leaves nothing dirty;
 *  • a value that landed in a partially-failed save stops showing as unsaved.
 *
 * `serverOf` returning `undefined` means the row is GONE (team deleted, member
 * removed while the form was open). Those keys are dropped rather than saved,
 * because the endpoint would either 404 or resurrect a cap for someone who is
 * no longer there.
 */
export function dirtyCapKeys(
  draft: CapDraft,
  serverOf: (key: string) => BudgetCap | undefined,
): string[] {
  return Object.keys(draft).filter((k) => {
    const server = serverOf(k)
    return server !== undefined && draft[k] !== server
  })
}

/**
 * Seed the alert-threshold controls from the server read-back.
 *
 *  • `null` (never configured) → the documented defaults.
 *  • a stored object → its exact values, including an empty `pct` array, which
 *    means "no percentage alerts" and must NOT fall back to the defaults.
 *
 * `absUsd` is microdollars on the wire; the input edits dollars, so it is
 * converted here (the inverse of what `saveThresholds` sends).
 */
export function seedThresholds(
  thresholds: OrgUsageResponse['thresholds'],
): { pct: number[]; absText: string } {
  if (!thresholds) return { pct: [...DEFAULT_THRESHOLD_PCT], absText: '' }
  // `pct: []` is a real state (alerts disabled) and must survive; only a
  // genuinely absent key (legacy rows saved before both arrays were sent)
  // falls back to the defaults.
  const pct = thresholds.pct ?? [...DEFAULT_THRESHOLD_PCT]
  const absText = (thresholds.absUsd ?? []).map((micro) => String(toDollars(micro))).join(', ')
  return { pct: [...pct], absText }
}

/**
 * The explicit role-cap override from the JSONB read-back, or `undefined` when
 * the role has no override (so it falls back to its hardcoded per-role default).
 *
 * `null` is a real value here — an override TO unlimited — and is distinct from
 * `undefined` (no override at all). The window-keyed JSONB wins over the legacy
 * `capBillingMicro` scalar when present, matching the server's own precedence.
 */
export function roleCapFromEntry(entry: RoleCapEntry | undefined): BudgetCap | undefined {
  if (!entry) return undefined
  if (entry.cap && 'month' in entry.cap) return entry.cap.month ?? null
  if (entry.capBillingMicro !== undefined) return entry.capBillingMicro
  return undefined
}

/** One Role caps row: the value shown, and whether it was read back from the
 *  tenant (an override, or a member holding the role) or is the bare default. */
export interface RoleCapSlot {
  cap: BudgetCap
  observed: boolean
}

/**
 * Seed the Role caps table from the `/usage` read-back.
 *
 * Precedence, per role:
 *  1. the explicit override (`roleCaps`) — authoritative, even for a role no
 *     member holds;
 *  2. the cap enforcement resolved for a member holding the role
 *     (`caps.components.roleCap`) — the server's own answer;
 *  3. neither: the role's DEFAULT, through `resolveRoleCap` — the SAME call
 *     `resolveCaps` makes, reading the ONE table in `@papan/types` (#767).
 *
 * Step 3 used to be `null` for every role. `null` is "unlimited", and guest's
 * default is 0, so an org with no guest showed Guest as "No ceiling" while the
 * 402 gave guests nothing — the permissive direction, on the screen an admin
 * reads to decide whether to set a cap at all. `role-cap-default-shown-enforced`
 * pins this against the real route and the real resolver.
 */
export function seedRoleCaps(
  data: Pick<OrgUsageResponse, 'roleCaps' | 'users'> | null | undefined,
): Record<string, RoleCapSlot> {
  const m: Record<string, RoleCapSlot> = {}
  for (const r of CAP_ROLES) {
    const override = roleCapFromEntry(data?.roleCaps?.[r.id])
    m[r.id] = override !== undefined
      ? { cap: override, observed: true }
      : { cap: resolveRoleCap(undefined, r.id), observed: false }
  }
  for (const u of data?.users ?? []) {
    const slot = m[u.role]
    if (!slot || slot.observed) continue
    slot.cap = u.caps.components.roleCap
    slot.observed = true
  }
  return m
}

/** ───── Team caps as a tree (#770) ───────────────────────────────────────
 *
 * Team contributions STACK down the ancestry: `resolveCaps` sums a member's
 * teams including every ancestor, so RM10 on a parent adds RM10 to every
 * member of every team beneath it. A flat table hid exactly that. The server
 * caps nesting at 10 levels (`MAX_TEAM_DEPTH`), which bounds the indent.
 */

/** The fields the tree needs; `TeamUsageRow` satisfies it. */
export interface TeamNode {
  id: string
  name: string
  parentId?: string | null
  priority?: number
}

export interface TeamTreeRow<T extends TeamNode = TeamNode> {
  team: T
  /** 0 for a root. */
  depth: number
  /** Ancestor ids, root first. A row is visible iff every one is expanded. */
  ancestorIds: string[]
  /** Every descendant id, depth-first — what collapsing this row hides. */
  descendantIds: string[]
  /** Direct children placed under this row (drives the chevron). */
  childCount: number
}

/** The Teams page's sibling order: priority descending (its drag order), then
 *  name, then id so the order is total and stable across reloads. */
function compareSiblings(a: TeamNode, b: TeamNode): number {
  return (b.priority ?? 0) - (a.priority ?? 0)
    || a.name.localeCompare(b.name)
    || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
}

/**
 * Flat `teams[]` → depth-first rows (parent, then its sub-teams in sibling
 * order), each with its depth, ancestors and descendants.
 *
 * NO ROW EVER DISAPPEARS. This is a table of money controls, so a team the
 * tree cannot place still renders, as a root:
 *  • a `parentId` missing from the payload (or absent entirely — an older API)
 *    is treated as no parent;
 *  • a parent cycle (the repository refuses one on write, but a row must not
 *    vanish if one exists) has no root to be reached from, so any team left
 *    unplaced after the walk is emitted as a root.
 */
export function buildTeamTree<T extends TeamNode>(teams: readonly T[]): TeamTreeRow<T>[] {
  const ids = new Set(teams.map((t) => t.id))
  const children = new Map<string | null, T[]>()
  for (const t of teams) {
    const parent = t.parentId && t.parentId !== t.id && ids.has(t.parentId) ? t.parentId : null
    const list = children.get(parent)
    if (list) list.push(t)
    else children.set(parent, [t])
  }
  for (const list of children.values()) list.sort(compareSiblings)

  const out: TeamTreeRow<T>[] = []
  const placed = new Set<string>()
  const visit = (team: T, depth: number, ancestorIds: string[]): TeamTreeRow<T> => {
    placed.add(team.id)
    const row: TeamTreeRow<T> = { team, depth, ancestorIds, descendantIds: [], childCount: 0 }
    out.push(row)
    for (const kid of children.get(team.id) ?? []) {
      if (placed.has(kid.id)) continue
      const kidRow = visit(kid, depth + 1, [...ancestorIds, team.id])
      row.childCount += 1
      row.descendantIds.push(kid.id, ...kidRow.descendantIds)
    }
    return row
  }
  for (const root of children.get(null) ?? []) visit(root, 0, [])
  for (const t of [...teams].sort(compareSiblings)) if (!placed.has(t.id)) visit(t, 0, [])
  return out
}

/**
 * Explicit expand/collapse choices, per team id, for this page visit only.
 * Anything not toggled takes the default below, so a team that appears on a
 * reload needs no seeding.
 */
export type TeamExpansion = Record<string, boolean>

/** Default: roots open (their direct sub-teams show), everything deeper closed. */
function expandedByDefault(depth: number): boolean {
  return depth === 0
}

export function isTeamExpanded(row: TeamTreeRow, toggled: TeamExpansion): boolean {
  return toggled[row.team.id] ?? expandedByDefault(row.depth)
}

/** The rows on screen: those whose every ancestor is expanded. */
export function visibleTeamRows<T extends TeamNode>(rows: TeamTreeRow<T>[], toggled: TeamExpansion): TeamTreeRow<T>[] {
  const byId = new Map(rows.map((r) => [r.team.id, r]))
  return rows.filter((r) => r.ancestorIds.every((id) => {
    const a = byId.get(id)
    return a ? isTeamExpanded(a, toggled) : true
  }))
}

/**
 * What a COLLAPSED row must still say about the sub-teams it hides — so a
 * collapsed row never hides a pending change from the page Save, or a
 * contribution that is quietly raising the cap of everyone beneath it.
 *
 *  • `hidden`  — how many sub-teams (all depths) are out of sight;
 *  • `unsaved` — how many of them carry a staged edit;
 *  • `funded`  — how many of them contribute a non-zero amount, measured on
 *                the value SHOWN (staged if staged), since that is what Save
 *                will write.
 *
 * Staged edits in a hidden row are still in the draft, so Save commits them
 * regardless of visibility; this is about the admin being able to SEE that.
 */
export function hiddenSubtreeHint(
  row: TeamTreeRow,
  isUnsaved: (teamId: string) => boolean,
  contributionOf: (teamId: string) => BudgetCap | undefined,
): { hidden: number; unsaved: number; funded: number } {
  let unsaved = 0
  let funded = 0
  for (const id of row.descendantIds) {
    if (isUnsaved(id)) unsaved += 1
    const c = contributionOf(id)
    if (typeof c === 'number' && c !== 0) funded += 1
  }
  return { hidden: row.descendantIds.length, unsaved, funded }
}

/**
 * Whether a "Spend by bucket" tile shows a money value (0.00 included) or '—'.
 *
 * A bucket is shown when the tenant HAS that funding source (`applicable`), OR
 * when it actually funded something this window (`spend > 0`) — a bucket that
 * paid for work must show its amount even if the source has since lapsed. `—`
 * only when neither holds, i.e. the source does not apply to this tenant and
 * spent nothing. `applicable` is `undefined` on a payload from an older API
 * that omits the flag; that falls back to SHOWN, never a wrong '—'.
 *
 * `free` never calls this — the included allowance always applies.
 */
export function bucketApplies(
  applicable: boolean | undefined,
  spendMicro: number | undefined,
): boolean {
  return (applicable ?? true) || (spendMicro ?? 0) > 0
}
